import { ComponentPropsWithoutRef } from 'react';

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
    outline?: boolean;
    disabled?: boolean;
    small?: boolean;
    children: React.ReactNode;
    type?: ComponentPropsWithoutRef<'button'>['type']; // Extract type dynamically
    fullWidth?: boolean;
}

/**
 * Button component with styling for different variants.
 *
 * @param variant - Primary, secondary, danger or ghost styling.
 * @param outline - Whether to use outline styling.
 * @param disabled - Whether button is disabled.
 * @param small - Whether to use small styling.
 * @param type - Type of the button (submit - button - reset)
 * @param children - Button contents.
 * @param rest - Other props passed to button.
 * @param fullWidth - Whether to use 100% of width
 */
export function Button({
    variant = 'primary',
    outline = false,
    disabled = false,
    small = false,
    type = 'button', // Default to "button" to prevent unintended form submissions
    fullWidth = false,
    children,
    ...rest
}: ButtonProps) {
    const baseClasses = 'btn self-start';
    const variantClasses = {
        primary: !outline ? 'btn-primary' : 'btn-primary-stroke',
        secondary: !outline ? 'btn-secondary' : 'btn-secondary-stroke',
        danger: 'btn-danger',
        ghost: 'btn-ghost',
    };

    const smallClasses = small ? 'btn-sm' : '';
    const fullWidthClass = fullWidth ? 'w-full' : '';

    const btnClasses = `${baseClasses} ${variantClasses[variant]} ${smallClasses} ${fullWidthClass} ${rest.className ?? ''}`;

    return (
        <button
            {...rest}
            type={type}
            className={btnClasses}
            disabled={disabled}
        >
            {children}
        </button>
    );
}
